// Core packages
import { OnDestroy } from "@angular/core";
// Third party packages
import { BehaviorSubject } from "rxjs";
// Custom packages
import { ApiService } from "./api.service";
import * as i0 from "@angular/core";
import * as i1 from "./api.service";
/** How often the counter is read again while it runs */
export var ARTURO_QUEUE_COUNT_INTERVAL_MS = 5 * 60 * 1000;
/**
 * The number of open proposals in the answer of `GET /api/v2/arturo/status` (`data.queue.open`, the
 * "Da confermare" counter of the menu), or null when the answer is not one: a failed call, which
 * `ApiService` emits as an `Error` value, included.
 *
 * @since 3.0.0
 */
export function arturoQueueOpenCount(res) {
    var envelope = res && typeof res === "object" ? res : null;
    if (!envelope || envelope.status !== true || !envelope.data || typeof envelope.data !== "object") {
        return null;
    }
    var queue = envelope.data.queue;
    var open = queue && typeof queue === "object" ? queue.open : null;
    return typeof open === "number" && isFinite(open) && Math.floor(open) === open && open >= 0 ? open : null;
}
/**
 * Whether a failed read of the counter can only fail again: a role the back-end does not let in (403).
 * Any other failure is retried at the next round: an expired session (401) once the user renews it
 * elsewhere, the module off (404) once it is on again, a deploy or a network failure once they pass.
 *
 * @since 3.0.0
 */
export function isArturoQueueCountFinal(err) {
    if (!err || typeof err !== "object" || err instanceof Error) {
        return false;
    }
    return err.status === 403;
}
/**
 * The "Da confermare" counter of the sidebar: the open proposals of Arturo's queue, read every
 * ARTURO_QUEUE_COUNT_INTERVAL_MS while it runs, one request at a time.
 *
 * It lives in `shared` and talks to the back-end through `ApiService` only, so that the sidebar,
 * which is in the initial bundle, does not pull Arturo's lazy module in. The sidebar starts it for the
 * roles the back-end lets in, and stops it otherwise; the queue page asks a new read after an action.
 *
 * @since 3.0.0
 */
var ArturoQueueCountService = /** @class */ (function () {
    /**
     * Class constructor
     */
    function ArturoQueueCountService(apiService) {
        this.apiService = apiService;
        /** The open proposals; 0 while unknown or stopped */
        this.count$ = new BehaviorSubject(0);
        this.running = false;
        this.request = null;
        this.timer = null;
    }
    /**
     * Stop on destroy: no timer outlives the application
     *
     * @since 3.0.0
     */
    ArturoQueueCountService.prototype.ngOnDestroy = function () {
        this.stop();
        this.count$.complete();
    };
    /**
     * Start reading the counter, now and then every interval; nothing when it already runs
     *
     * @since 3.0.0
     */
    ArturoQueueCountService.prototype.start = function () {
        if (this.running) {
            return;
        }
        this.running = true;
        this.refresh();
    };
    /**
     * Stop reading the counter, and show none
     *
     * @since 3.0.0
     */
    ArturoQueueCountService.prototype.stop = function () {
        this.running = false;
        this.cancel();
        if (this.count$.value !== 0) {
            this.count$.next(0);
        }
    };
    /**
     * Read the counter now, when it runs: after an action of the queue, for instance. A read in flight
     * or scheduled is replaced.
     *
     * @since 3.0.0
     */
    ArturoQueueCountService.prototype.refresh = function () {
        var _this = this;
        if (!this.running) {
            return;
        }
        this.cancel();
        // Never renews the session (autoRefresh = false): a read every few minutes, on its own, would keep
        // an idle tab logged in for the whole life of the refresh token. An expired session answers 401,
        // and the next round tries again.
        this.request = this.apiService
            .apiCall("GET", "arturo/status", undefined, undefined, false, undefined, "v2")
            .subscribe(function (res) {
            _this.request = null;
            var open = arturoQueueOpenCount(res);
            if (open !== null) {
                _this.count$.next(open);
            }
            _this.schedule();
        }, function (err) {
            _this.request = null;
            if (isArturoQueueCountFinal(err)) {
                _this.stop();
                return;
            }
            _this.schedule();
        });
    };
    /**
     * The next read, after the interval
     *
     * @since 3.0.0
     */
    ArturoQueueCountService.prototype.schedule = function () {
        var _this = this;
        if (!this.running) {
            return;
        }
        this.timer = setTimeout(function () {
            _this.timer = null;
            _this.refresh();
        }, ARTURO_QUEUE_COUNT_INTERVAL_MS);
    };
    /**
     * Cancel the read in flight and the next one
     *
     * @since 3.0.0
     */
    ArturoQueueCountService.prototype.cancel = function () {
        if (this.timer !== null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        if (this.request) {
            this.request.unsubscribe();
            this.request = null;
        }
    };
    ArturoQueueCountService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function ArturoQueueCountService_Factory() { return new ArturoQueueCountService(i0.ɵɵinject(i1.ApiService)); }, token: ArturoQueueCountService, providedIn: "root" });
    return ArturoQueueCountService;
}());
export { ArturoQueueCountService };
